/**
 * Drawing the maze on a `<canvas>`. Twin of `app/lib/board_view.dart`.
 *
 * The palette matches the Flutter app's `BoardColors` exactly so the two
 * clients read as one game, not as one game and its lookalike.
 */

import { canMove, topStart, bottomStart, type Maze } from '../../shared/maze.js';
import { GameModel } from './game.js';

// The palette. Kept here rather than in CSS because canvas draws with these
// numbers directly, and the game colours are properties of the board, not of
// the app chrome. Values are from `app/lib/board_view.dart` unchanged.
export const boardColors = {
  background: '#0E1116',
  fog: '#171C23',
  floor: '#232A34',
  wall: '#7C8A9C',
  you: '#4CC2FF',
  opponent: '#FF9F45',
  goal: '#6BE58A',
} as const;

// Direction constants in the maze's own space. Kept here so the renderer can
// name them without importing the whole maze module twice.
const NORTH = 0;
const EAST = 1;
const SOUTH = 2;
const WEST = 3;

/** Draws the board onto a `<canvas>` from a `GameModel` snapshot. */
export class BoardRenderer {
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly model: GameModel;
  /**
   * The last frame we drew. `requestAnimationFrame` calls back at 60Hz even
   * while nothing moves; skipping identical frames is what lets a laptop
   * fan sit still while a player is thinking.
   */
  private lastKey = '';

  constructor(canvas: HTMLCanvasElement, model: GameModel) {
    this.canvas = canvas;
    this.model = model;
    const ctx = canvas.getContext('2d');
    if (ctx === null) {
      throw new Error('this browser has no 2D canvas support');
    }
    this.ctx = ctx;
  }

  /**
   * Redraw if anything visible changed.
   *
   * Called on every model notify and once from a `ResizeObserver`; the
   * key comparison is what keeps the two hooks cheap.
   */
  render(): void {
    const key = this.dedupeKey();
    if (key === this.lastKey) return;
    this.lastKey = key;
    this.paint();
  }

  /** Force the next `render` to paint even if the model looks unchanged. */
  invalidate(): void {
    this.lastKey = '';
  }

  private dedupeKey(): string {
    return [
      this.model.maze?.size ?? 0,
      this.model.you,
      this.model.opponent,
      this.model.revealed.size,
      this.canvas.width,
      this.canvas.height,
      this.model.role ?? 'none',
    ].join('|');
  }

  private paint(): void {
    const { ctx, canvas, model } = this;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const maze = model.maze;
    if (maze === null) return;

    const n = maze.size;
    const shortest = Math.min(canvas.width, canvas.height);
    const cellSize = shortest / n;
    const originX = (canvas.width - cellSize * n) / 2;
    const originY = (canvas.height - cellSize * n) / 2;

    // Pass one: the floor. Every cell, so the fog reads as unlit ground
    // rather than as a hole in the board.
    for (let i = 0; i < n * n; i++) {
      const view = model.toViewCell(i);
      const [x, y] = this.cellXY(view, n, cellSize, originX, originY);
      ctx.fillStyle = model.isVisible(i) ? boardColors.floor : boardColors.fog;
      ctx.fillRect(x, y, cellSize, cellSize);
    }

    // The goal, under the walls so a wall crossing it still reads correctly.
    this.drawGoal(model.centerCell(), n, cellSize, originX, originY);

    // Pass two: the walls, only for cells the player has seen. A wall is
    // shared, so drawing it from both sides would double the stroke; drawing
    // it only from the visible side is also what gives the fog its edge.
    ctx.strokeStyle = boardColors.wall;
    ctx.lineCap = 'round';
    ctx.lineWidth = Math.max(1.5, Math.min(cellSize * 0.12, 4));

    for (let i = 0; i < n * n; i++) {
      if (!model.isVisible(i)) continue;
      const view = model.toViewCell(i);
      const [x, y] = this.cellXY(view, n, cellSize, originX, originY);

      // Directions are in view space: the board is rotated for the bottom
      // player, so the wall that is north in the maze is drawn north on
      // screen only after the same rotation the cell got.
      for (let screenDir = 0; screenDir < 4; screenDir++) {
        const mazeDir = model.toMazeDir(screenDir);
        if (canMove(maze, i, mazeDir)) continue;
        // Both entrances are drawn as gaps in the outer wall — the generator
        // does not carve them, so `canMove` refuses to step through one, but
        // showing a wall there would draw a closed maze.
        if (this.isEntrance(maze, i, mazeDir)) continue;
        this.drawWall(x, y, cellSize, screenDir);
      }
    }

    // The opponent first, so that on the last frame — when both stand on the
    // centre — the player still sees themselves.
    this.drawPawn(
      model.toViewCell(model.opponent),
      n,
      cellSize,
      originX,
      originY,
      boardColors.opponent,
    );
    this.drawPawn(
      model.toViewCell(model.you),
      n,
      cellSize,
      originX,
      originY,
      boardColors.you,
    );
  }

  private cellXY(
    view: number,
    n: number,
    cellSize: number,
    originX: number,
    originY: number,
  ): [number, number] {
    return [
      originX + (view % n) * cellSize,
      originY + Math.floor(view / n) * cellSize,
    ];
  }

  private drawGoal(
    cellIndex: number,
    n: number,
    cellSize: number,
    originX: number,
    originY: number,
  ): void {
    const model = this.model;
    if (cellIndex < 0) return;
    const view = model.toViewCell(cellIndex);
    const [x, y] = this.cellXY(view, n, cellSize, originX, originY);
    const inset = cellSize * 0.22;
    const r = cellSize * 0.12;
    const gx = x + inset;
    const gy = y + inset;
    const gw = cellSize - 2 * inset;
    const gh = cellSize - 2 * inset;
    this.ctx.fillStyle = boardColors.goal;
    this.ctx.globalAlpha = 0.9;
    this.roundRect(gx, gy, gw, gh, r);
    this.ctx.fill();
    this.ctx.globalAlpha = 1;
  }

  private drawWall(x: number, y: number, cellSize: number, screenDir: number): void {
    const { ctx } = this;
    ctx.beginPath();
    switch (screenDir) {
      case NORTH:
        ctx.moveTo(x, y);
        ctx.lineTo(x + cellSize, y);
        break;
      case EAST:
        ctx.moveTo(x + cellSize, y);
        ctx.lineTo(x + cellSize, y + cellSize);
        break;
      case SOUTH:
        ctx.moveTo(x, y + cellSize);
        ctx.lineTo(x + cellSize, y + cellSize);
        break;
      case WEST:
        ctx.moveTo(x, y);
        ctx.lineTo(x, y + cellSize);
        break;
    }
    ctx.stroke();
  }

  private isEntrance(maze: Maze, cell: number, mazeDir: number): boolean {
    return (
      (cell === topStart(maze) && mazeDir === NORTH) ||
      (cell === bottomStart(maze) && mazeDir === SOUTH)
    );
  }

  private drawPawn(
    view: number,
    n: number,
    cellSize: number,
    originX: number,
    originY: number,
    color: string,
  ): void {
    const [x, y] = this.cellXY(view, n, cellSize, originX, originY);
    const cx = x + cellSize / 2;
    const cy = y + cellSize / 2;
    this.ctx.fillStyle = color;
    this.ctx.beginPath();
    this.ctx.arc(cx, cy, cellSize * 0.3, 0, Math.PI * 2);
    this.ctx.fill();
  }

  private roundRect(
    x: number,
    y: number,
    w: number,
    h: number,
    r: number,
  ): void {
    const { ctx } = this;
    const radius = Math.min(r, w / 2, h / 2);
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + w - radius, y);
    ctx.arcTo(x + w, y, x + w, y + radius, radius);
    ctx.lineTo(x + w, y + h - radius);
    ctx.arcTo(x + w, y + h, x + w - radius, y + h, radius);
    ctx.lineTo(x + radius, y + h);
    ctx.arcTo(x, y + h, x, y + h - radius, radius);
    ctx.lineTo(x, y + radius);
    ctx.arcTo(x, y, x + radius, y, radius);
    ctx.closePath();
  }
}
